/* eslint-disable @typescript-eslint/no-unused-vars */
import { useModelViewerScript } from 'lib/utils/use-model-viewer-script';
import { useEffect, useState } from 'react';
import { Feature } from 'src/.generated/Feature.EnterpriseWeb.model';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import { isDesktop } from 'react-device-detect';
import Headline from 'src/helpers/Headline/Headline';
import classNames from 'classnames';
import SvgIcon, { IconTypes } from '../SvgIcon/SvgIcon';
import { getEnum } from 'lib/utils/get-enum';

export type ARProductModalProps = Feature.EnterpriseWeb.RenewalByAndersen.Cards.ARProductCard & {
  modalId: string;
};

const ButtonSecondaryClasses = () => {
  return {
    btnClass:
      'group relative flex w-fit items-center border-2 border-theme-btn-secondary-border bg-theme-btn-secondary-bg p-[14px] font-serif text-button font-bold text-theme-btn-secondary-text hover:bg-theme-btn-secondary-bg-hover hover:text-theme-btn-secondary-text-hover disabled:bg-gray disabled:border-gray disabled:text-dark-gray disabled:cursor-not-allowed  w-[150px] h-[55px]',
    iconClass: 'ml-[10px] text-primary group-hover:text-primary group-disabled:text-dark-gray pr-2',
  };
};

export const ButtonTertiaryClasses = () => {
  return {
    btnClass:
      'group relative flex w-fit items-center font-serif text-button font-bold text-theme-text after:absolute after:bottom-[-4px] after:hidden after:h-[3px] after:w-full after:bg-theme-btn-icon-hover hover:after:inline-block disabled:text-gray pl-4',
    iconClass:
      'ml-[10px] text-theme-btn-icon group-hover:text-theme-btn-icon-hover group-hover:underline group-hover:decoration-theme-btn-icon-hover group-hover:underline-offset-8',
  };
};

const ARProductModal = ({ modalId, ...props }: ARProductModalProps) => {
  const { fields } = props;
  // Add the Modal Viewer script
  useModelViewerScript();
  const [isARModalOpen, setIsModalOpen] = useState(props.isModalOpen);
  const [angle, setAngle] = useState(0);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [isTextSent, setTextSent] = useState(false);
  const _icon = getEnum<IconTypes>(fields?.buttonIcon);
  const [pageUrl, setPageUrl] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleOrientationChange = () => {
    setAngle(window.screen.orientation.angle);
  };

  const toggleModal = () => {
    const arModalElement = document.getElementById(modalId);
    arModalElement?.classList.toggle('hidden');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: fields.shareSubject.value,
          text: fields.shareMessage.value.replace('{link}', ''),
          url: pageUrl + '?ar=true&modalId=' + modalId,
        });
        console.log('Content shared successfully!');
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      console.log('Web Share API is not supported in this browser.');
    }
  };

  const handleClose = () => {
    //Reset everything to allow fresh new modal session
    setIsModalOpen(false);
    setPhone('');
    setEmail('');
    setErrorMsg('');
    setTextSent(false);
    setEmailSent(false);
    toggleModal();
  };

  useEffect(() => {
    setPageUrl(window.location.href);
    if (pageUrl.includes('ar=true')) {
      const urlParams = pageUrl.split('&');
      const urlModalId = new URLSearchParams(urlParams.find((item) => item.startsWith('modalId')));
      const newModalId = urlModalId.get('modalId');
      if (modalId === newModalId) {
        setIsModalOpen(true);
      }
    }

    if (screen.orientation) {
      setAngle(window.screen.orientation.angle);
      // Property doesn't exist on screen in IE11
      screen.orientation.addEventListener('change', handleOrientationChange);
    }

    return () => {
      window.removeEventListener('change', handleOrientationChange);
    };
  }, [pageUrl]);

  const desktopMessage = () => {
    return (
      <>
        <div className="bottom-[180px] mt-8 block p-4 font-sans text-[12px] leading-[17px] md:absolute">
          {fields?.desktopMessage?.value}
        </div>
      </>
    );
  };

  const openAR = () => {
    const productModal = document.querySelector('#' + modalId);
    const modelViewer = productModal?.querySelector('model-viewer');
    modelViewer?.activateAR();
  };
  const mobileCTAs = () => {
    return (
      <>
        <div>
          <div className="flex justify-center">
            <div className="mt-s flex flex-row items-start items-center items-center justify-center gap-4">
              {fields?.buttonText && (
                <div className="">
                  <button
                    slot="ar-button"
                    onClick={() => {
                      openAR();
                    }}
                    className={classNames(ButtonSecondaryClasses().btnClass)}
                  >
                    <SvgIcon icon={_icon} className={ButtonSecondaryClasses().iconClass} />
                    <span>{fields?.buttonText?.value}</span>
                  </button>
                </div>
              )}
              <div className="">
                <button
                  onClick={() => {
                    handleShare();
                  }}
                  className={classNames(
                    ButtonSecondaryClasses().btnClass,
                    'justify-center p-[16px]'
                  )}
                >
                  <SvgIcon
                    icon="share"
                    className={classNames(ButtonSecondaryClasses().iconClass)}
                  />
                  <span>Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const productProperties = () => {
    return (
      <>
        <div className={classNames('mb-8 w-full columns-2 gap-2 pr-2 md:gap-4 md:pr-0')}>
          <div className=" flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="flex w-[70px] font-medium md:w-auto">Exterior Color</div>
            <div className="w-[75px] pl-[12px] text-dark-gray md:w-auto md:pl-[29px]">
              {fields?.exteriorColor?.value}
            </div>
          </div>
          <div className="mt-2 flex  pr-2 font-sans text-[14px] leading-[17px]">
            <div className="flex w-[70px] font-medium md:w-auto">Interior Color</div>
            <div className="w-[75px] pl-[12px] text-dark-gray md:w-auto md:pl-[32px]">
              {fields?.interiorColor?.value}
            </div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="flex w-[70px] font-medium md:w-auto">Hardware Style</div>
            <div className="w-[75px] pl-[12px] text-dark-gray md:w-auto md:pl-[16px]">
              {fields?.hardwareStyle?.value}
            </div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px]  leading-[17px]">
            <div className="flex w-[70px] font-medium md:w-auto">Hardware Color</div>
            <div className="w-[75px] pl-[12px] text-dark-gray md:w-auto md:pl-[16px]">
              {fields?.hardwareColor?.value}
            </div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="flex w-[70px] font-medium md:w-auto">Grille Style</div>
            <div className="w-[75px] pl-2 text-dark-gray md:w-auto md:pl-[32px]">
              {fields?.grilleStyle?.value}
            </div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="flex w-[70px] font-medium md:w-auto">Product Type</div>
            <div className="w-[75px] pl-2 text-dark-gray md:w-auto md:pl-4">
              {fields?.productType?.value}
            </div>
          </div>
          <div className="mt-2 flex pr-2 font-sans text-[14px] leading-[17px]">
            <div className="flex w-[70px] font-medium md:w-auto">Product Size</div>
            <div className="w-[75px] pl-2 text-dark-gray md:w-auto md:pl-[20px]">
              {fields?.productSize?.value}
            </div>
          </div>
        </div>
      </>
    );
  };

  const sendTextMsg = async () => {
    const phoneRegex = /^\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    if (phone && phoneRegex.test(phone)) {
      const data = {
        phoneNumber: phone,
        link: pageUrl + '?ar=true&modalId=' + modalId,
      };

      try {
        const response = await fetch(`/api/rba/submit-actions/send-text`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (response.ok) {
          setTextSent(true);
          setErrorMsg('');
        } else {
          console.error('Error:', response.statusText);
          setErrorMsg('Error sending message.');
        }
      } catch (error) {
        console.error('Error sending message:', error);
        setErrorMsg('Error sending message.');
      }
    } else {
      setErrorMsg('Please enter a valid phone number.');
    }
  };

  const sendEmail = async () => {
    const emailRegex = /^[A-Za-z0-9._%-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
    if (email && emailRegex.test(email)) {
      const data = {
        to: [email],
        from: fields.emailFrom.value,
        subject: fields.shareSubject.value,
        body: fields.shareMessage.value.replace('{link}', pageUrl + '?ar=true&modalId=' + modalId),
        isHtml: true,
      };

      try {
        const response = await fetch(`/api/enterprise/submit-actions/send-email/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });

        if (response.ok) {
          setEmailSent(true);
          setErrorMsg('');
        } else {
          console.error('Error:', response.statusText);
          setErrorMsg('Error sending message.');
        }
      } catch (error) {
        console.error('Error sending message:', error);
        setErrorMsg('Error sending message.');
      }
    } else {
      setErrorMsg('Please enter a valid email.');
    }
  };

  return (
    <>
      <ModalWrapper
        isModalOpen={isARModalOpen}
        size={angle === 90 ? 'extra-large' : 'fluid'}
        handleClose={handleClose}
        modalLabel={modalId}
        customOverlayclass="flex"
      >
        <div className="flex flex-col md:w-[840px]">
          <div>
            {!isDesktop && (
              <div>
                <div className="flex flex-col px-4 pb-4">
                  <model-viewer
                    src={fields?.gltfLink?.value.href}
                    ios-src={fields?.usdzLink?.value.href}
                    ar
                    camera-controls=""
                    quick-look-browsers="safari chrome"
                    camera-orbit="0deg 90deg"
                    poster={fields?.posterImage?.value?.src}
                    tabindex="0"
                    class="mb-[15px] ml-auto mr-auto min-h-[350px] w-full self-center overflow-visible pr-8"
                    ar-status="not-presenting"
                  ></model-viewer>

                  <div className="flex flex-col">
                    <div className="flex flex-wrap">
                      <div className="mb-8 w-full"> {mobileCTAs()}</div>
                      <Headline
                        useTag="h2"
                        fields={{ headlineText: { value: fields?.productName?.value } }}
                        classes={classNames('text-theme-text text-sm-s font-medium mb-4')}
                      />
                      {productProperties()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div>
            {isDesktop && (
              <div>
                <div className="flex flex-col px-4 pb-4 md:flex-row">
                  <model-viewer
                    src={fields?.gltfLink?.value.href}
                    ios-src={fields?.usdzLink?.value.href}
                    camera-controls=""
                    quick-look-browsers="safari chrome"
                    camera-orbit="0deg 90deg"
                    poster={fields?.posterImage?.value?.src}
                    tabindex="0"
                    class="mb-[15px] ml-auto mr-auto min-h-[350px] w-full self-center pr-8 md:w-[650px]"
                    ar-status="not-presenting"
                  ></model-viewer>

                  <div className="flex flex-col">
                    <div className="flex flex-wrap  md:w-[462px]">
                      <Headline
                        useTag="h2"
                        fields={{ headlineText: { value: fields?.productName?.value } }}
                        classes={classNames('text-theme-text text-sm-s font-medium lg:text-m mb-s')}
                      />
                      {productProperties()}
                      {desktopMessage()}
                    </div>
                  </div>
                </div>
                <div className="ml-2 grid w-[800px] grid-cols-1 gap-4 p-4 ml:grid-cols-2">
                  {!isTextSent ? (
                    <div className={classNames('flex w-[300px] flex-col items-start gap-2')}>
                      <div className=" text-sm-xs">
                        Provide your mobile number to receive the text link to start the augmented
                        reality experience.
                      </div>
                      <div className=" text-sm-xxs">Mobile Number</div>
                      <div className="flex flex-row">
                        <input
                          type="tel"
                          placeholder="xxx-xxx-xxxx"
                          className=" rounded-sm border border-[#b9b9b9]"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                        <button
                          onClick={sendTextMsg}
                          className={classNames(ButtonTertiaryClasses().btnClass)}
                        >
                          <span>Submit</span>
                          <SvgIcon
                            icon="arrow"
                            className={classNames(ButtonTertiaryClasses().iconClass)}
                          />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">Text Message Sent!</div>
                  )}
                  {!emailSent ? (
                    <div
                      className={classNames(
                        'flex w-[300px] flex-col items-start gap-2',
                        isDesktop
                          ? ' border-l border-l-gray pl-[4rem]'
                          : ' mt-8 border-t border-t-gray pt-8'
                      )}
                    >
                      <div className=" text-sm-xs">
                        OR - You can provide your email address and we will send you the link to
                        your email.
                      </div>
                      <div className=" text-sm-xxs">Email address</div>
                      <div className="flex flex-row">
                        <input
                          type="email"
                          placeholder="name@email.com"
                          className=" rounded-sm border border-[#b9b9b9]"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                        <button
                          onClick={sendEmail}
                          className={classNames(ButtonTertiaryClasses().btnClass)}
                        >
                          <span>Submit</span>
                          <SvgIcon
                            icon="arrow"
                            className={classNames(ButtonTertiaryClasses().iconClass)}
                          />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">Email Sent!</div>
                  )}
                </div>
                {errorMsg && (
                  <div className="mt-2 block p-2 font-sans text-[14px] leading-[17px] text-error">
                    {errorMsg}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};

export default ARProductModal;
